import { useCallback, useEffect, useMemo, useReducer } from 'react';
import { message } from 'antd';
import { v4 as uuidv4 } from 'uuid';
import type {
  OrderItem,
  OrderType,
  FulfillmentMode,
  PaymentMethod,
  CommissionStaff,
} from '../types';

export interface PosTabState {
  id: string;
  name: string;
  orderItems: OrderItem[];
  orderType: OrderType;
  fulfillmentMode: FulfillmentMode;
  paymentMethod: PaymentMethod;
  selectedSessionId?: string;
  commissionStaff: CommissionStaff[];
  customerForm: Record<string, unknown>;
  /** Tiền khách đưa (tab cash hoặc split) — display-only FE. */
  cashReceived?: number;
  /** Account đã chọn (bank/ewallet/card) — display-only FE. */
  paymentAccountId?: string;
  /** Số tiền trả qua phương thức phi tiền mặt (split) — display-only FE. */
  nonCashAmount?: number;
}

export interface PosTabsState {
  tabs: PosTabState[];
  activeId: string;
}

export type PosTabsAction =
  | { type: 'ADD_TAB' }
  | { type: 'CLOSE_TAB'; id: string }
  | { type: 'SWITCH_TAB'; id: string }
  | { type: 'PATCH_ACTIVE'; patch: Partial<Omit<PosTabState, 'id' | 'name'>> }
  | { type: 'CLEAR_ALL' }
  | { type: 'HYDRATE'; state: PosTabsState };

export const MAX_TABS = 10;
export const STORAGE_KEY = 'pos-tabs-v1';

export function createEmptyTab(name: string): PosTabState {
  return {
    id: uuidv4(),
    name,
    orderItems: [],
    orderType: 'POS',
    fulfillmentMode: 'INSTANT',
    paymentMethod: 'bank',
    selectedSessionId: undefined,
    commissionStaff: [],
    customerForm: {},
    cashReceived: undefined,
    paymentAccountId: undefined,
    nonCashAmount: undefined,
  };
}

export function getInitialState(): PosTabsState {
  const first = createEmptyTab('Đơn hàng 1');
  return { tabs: [first], activeId: first.id };
}

function nextTabName(tabs: PosTabState[]): string {
  let maxIdx = 0;
  for (const t of tabs) {
    const m = t.name.match(/^Đơn hàng (\d+)$/);
    if (m) {
      const n = parseInt(m[1], 10);
      if (n > maxIdx) maxIdx = n;
    }
  }
  return `Đơn hàng ${maxIdx + 1}`;
}

function isValidTab(t: unknown): t is PosTabState {
  if (typeof t !== 'object' || t === null) return false;
  const obj = t as Record<string, unknown>;
  return (
    typeof obj.id === 'string' &&
    typeof obj.name === 'string' &&
    Array.isArray(obj.orderItems) &&
    typeof obj.orderType === 'string' &&
    typeof obj.fulfillmentMode === 'string' &&
    typeof obj.paymentMethod === 'string' &&
    Array.isArray(obj.commissionStaff) &&
    typeof obj.customerForm === 'object' && obj.customerForm !== null
  );
}

function hydrateFromStorage(): PosTabsState | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return null;
    const obj = parsed as Record<string, unknown>;
    if (!Array.isArray(obj.tabs) || obj.tabs.length === 0) return null;
    const tabs = obj.tabs.filter(isValidTab) as PosTabState[];
    if (tabs.length === 0) return null;
    const activeIdRaw = typeof obj.activeId === 'string' ? obj.activeId : null;
    const activeId = activeIdRaw && tabs.some((t) => t.id === activeIdRaw) ? activeIdRaw : tabs[0].id;
    return { tabs, activeId };
  } catch {
    return null;
  }
}

export interface UsePosTabsReturn {
  state: PosTabsState;
  active: PosTabState;
  addTab: () => void;
  closeTab: (id: string) => void;
  switchTab: (id: string) => void;
  patchActive: (patch: Partial<Omit<PosTabState, 'id' | 'name'>>) => void;
  clearAll: () => void;
}

export function reducer(state: PosTabsState, action: PosTabsAction): PosTabsState {
  switch (action.type) {
    case 'ADD_TAB': {
      if (state.tabs.length >= MAX_TABS) return state;
      const newTab = createEmptyTab(nextTabName(state.tabs));
      return { tabs: [...state.tabs, newTab], activeId: newTab.id };
    }
    case 'CLOSE_TAB': {
      const idx = state.tabs.findIndex((t) => t.id === action.id);
      if (idx === -1) return state;
      const nextTabs = state.tabs.filter((t) => t.id !== action.id);
      if (nextTabs.length === 0) {
        const empty = createEmptyTab('Đơn hàng 1');
        return { tabs: [empty], activeId: empty.id };
      }
      let nextActive = state.activeId;
      if (action.id === state.activeId) {
        const leftIdx = Math.max(0, idx - 1);
        nextActive = nextTabs[leftIdx].id;
      }
      return { tabs: nextTabs, activeId: nextActive };
    }
    case 'SWITCH_TAB': {
      if (!state.tabs.some((t) => t.id === action.id)) return state;
      if (state.activeId === action.id) return state;
      return { ...state, activeId: action.id };
    }
    case 'PATCH_ACTIVE': {
      return {
        ...state,
        tabs: state.tabs.map((t) => (t.id === state.activeId ? { ...t, ...action.patch } : t)),
      };
    }
    case 'CLEAR_ALL':
      return getInitialState();
    case 'HYDRATE':
      return action.state;
    default:
      return state;
  }
}

export function usePosTabs(): UsePosTabsReturn {
  const [state, dispatch] = useReducer(
    reducer,
    undefined,
    () => hydrateFromStorage() ?? getInitialState(),
  );

  useEffect(() => {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch {
      // sessionStorage full or disabled — silently ignore
    }
  }, [state]);

  const active = useMemo(
    () => state.tabs.find((t) => t.id === state.activeId) ?? state.tabs[0],
    [state.tabs, state.activeId],
  );

  const addTab = useCallback(() => {
    if (state.tabs.length >= MAX_TABS) {
      void message.warning(`Tối đa ${MAX_TABS} đơn cùng lúc`);
      return;
    }
    dispatch({ type: 'ADD_TAB' });
  }, [state.tabs.length]);

  const closeTab = useCallback((id: string) => dispatch({ type: 'CLOSE_TAB', id }), []);
  const switchTab = useCallback((id: string) => dispatch({ type: 'SWITCH_TAB', id }), []);
  const patchActive = useCallback(
    (patch: Partial<Omit<PosTabState, 'id' | 'name'>>) =>
      dispatch({ type: 'PATCH_ACTIVE', patch }),
    [],
  );
  const clearAll = useCallback(() => dispatch({ type: 'CLEAR_ALL' }), []);

  return { state, active, addTab, closeTab, switchTab, patchActive, clearAll };
}
